import { Alert, Button, Input, Select, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type {
  ControlKind,
  ControlVerdict,
  EvaluationScenario,
  RelationKind,
  ToolDoubleOutcome,
} from '@/api/scenarioVersions';
import { StatusPill } from '@/components/StatusPill';
import { colors } from '@/theme/tokens';
import { CONTROL_WORDS, DOUBLE_OUTCOME_WORDS, RELATION_WORDS } from '../scenarioWords';
import { cardStyle } from '../review/reviewParts';
import { Field, sectionTitle, type SectionProps } from './editorSections';
import { addItem, newControlId, removeItem, setHazard, updateItem } from './scenarioEdits';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// The four parts contract version 3 adds to a scenario (2026-11-02), as editor sections: what the case rests on (its
// hazard and sources), what its tools answer (tool doubles), the replies that prove its checks (controls) and its
// siblings. Each edits the draft through scenarioEdits; the server checks every field and replays every control, and
// a control that misbehaves is a warning beside the save, never a block (founder, 2026-11-02).
// ─────────────────────────────────────────────────────────────────────────────

const turnOptions = (s: EvaluationScenario) =>
  [...new Set(s.events.map((e) => e.turnIndex))].sort((a, b) => a - b).map((t) => ({ value: t, label: `Turn ${t + 1}` }));

const row = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, alignItems: 'start' } as const;

function RemoveButton({ label, onClick, editable }: { label: string; onClick: () => void; editable: boolean }) {
  if (!editable) return null;
  return <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={onClick} aria-label={label} style={{ justifySelf: 'end' }} />;
}

// ── Hazard & sources ────────────────────────────────────────────────────────

export function HazardSourcesSection({ draft, onChange, editable, isDark }: SectionProps) {
  const sources = draft.sources ?? [];
  return (
    <section id="section-basis" aria-label="Hazard and sources">
      <Text style={sectionTitle}>Hazard &amp; sources</Text>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        What goes wrong for the patient if the agent fails this case, and what the expectations rest on. A reviewer checks the
        expectations against the sources; until one is named, every clinical expectation stays pending SME review.
      </Text>
      <Field label="Hazard · optional" hint="One sentence on the harm, e.g. A febrile patient on day 10 of FOLFOX is told to wait until morning.">
        <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} disabled={!editable} value={draft.hazard ?? ''} aria-label="Hazard"
          onChange={(e) => onChange(setHazard(draft, e.target.value))} />
      </Field>
      <Field label={`Sources · ${sources.length}`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sources.length === 0 && <Text type="secondary">No source is named.</Text>}
          {sources.map((src, i) => (
            <div key={i} style={{ ...cardStyle(isDark), padding: 10 }} aria-label={`Source ${i + 1}`}>
              <div style={row}>
                <Input size="small" disabled={!editable} placeholder="URL or source-register id" value={src.ref} aria-label="Source reference"
                  onChange={(e) => onChange(updateItem(draft, 'sources', i, { ref: e.target.value }))} />
                <Input size="small" disabled={!editable} placeholder="Section, e.g. §2.4 Fever" value={src.section ?? ''} aria-label="Source section"
                  onChange={(e) => onChange(updateItem(draft, 'sources', i, { section: e.target.value || undefined }))} />
                <Input size="small" disabled={!editable} placeholder="Revision or date" value={src.revision ?? ''} aria-label="Source revision"
                  onChange={(e) => onChange(updateItem(draft, 'sources', i, { revision: e.target.value || undefined }))} />
                <RemoveButton label={`Remove source ${i + 1}`} editable={editable} onClick={() => onChange(removeItem(draft, 'sources', i))} />
              </div>
              <Input size="small" disabled={!editable} placeholder="What it says that this case relies on (optional)" value={src.note ?? ''} aria-label="Source note"
                style={{ marginTop: 6 }} onChange={(e) => onChange(updateItem(draft, 'sources', i, { note: e.target.value || undefined }))} />
            </div>
          ))}
          {editable && (
            <Button size="small" icon={<PlusOutlined />} style={{ alignSelf: 'flex-start' }} onClick={() => onChange(addItem(draft, 'sources', { ref: '' }))}>
              Add a source
            </Button>
          )}
        </div>
      </Field>
    </section>
  );
}

// ── Tool answers ────────────────────────────────────────────────────────────

export function ToolAnswersSection({ draft, onChange, editable, isDark }: SectionProps) {
  const doubles = draft.toolDoubles ?? [];
  const turns = turnOptions(draft);
  return (
    <section id="section-doubles" aria-label="Tool answers">
      <Text style={sectionTitle}>Tool answers</Text>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Say what a tool answers in this case instead of running: a lab result, nothing on file, an error or a time-out. The
        tool never runs, and the run reports the call as answered by the scenario’s double, so the case tests how the agent
        handles that answer. A tool not named here behaves as in any isolated run.
      </Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {doubles.length === 0 && <Text type="secondary">No tool is answered for: every tool behaves as in any isolated run.</Text>}
        {doubles.map((d, i) => (
          <div key={i} style={{ ...cardStyle(isDark), padding: 10 }} aria-label={`Tool answer ${i + 1}`}>
            <div style={row}>
              <Input size="small" disabled={!editable} placeholder="The tool’s name, as the agent calls it" value={d.tool} aria-label="Tool"
                onChange={(e) => onChange(updateItem(draft, 'toolDoubles', i, { tool: e.target.value }))} />
              <Select<ToolDoubleOutcome> size="small" disabled={!editable} value={d.outcome} aria-label="What the tool does"
                onChange={(v) => onChange(updateItem(draft, 'toolDoubles', i, { outcome: v }))}
                options={(Object.keys(DOUBLE_OUTCOME_WORDS) as ToolDoubleOutcome[]).map((o) => ({ value: o, label: DOUBLE_OUTCOME_WORDS[o] }))} />
              <Select<number | 'every'> size="small" disabled={!editable} value={d.turnIndex ?? 'every'} aria-label="On which turn"
                onChange={(v) => onChange(updateItem(draft, 'toolDoubles', i, { turnIndex: v === 'every' ? undefined : v }))}
                options={[{ value: 'every' as const, label: 'Every turn' }, ...turns]} />
              <RemoveButton label={`Remove tool answer ${i + 1}`} editable={editable} onClick={() => onChange(removeItem(draft, 'toolDoubles', i))} />
            </div>
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 8 }} disabled={!editable} value={d.response} aria-label="What the tool returns"
              placeholder='What the tool returns to the agent, as text or JSON, e.g. {"anc": 0.4, "collected": "today 07:10"}'
              style={{ marginTop: 6, fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5 }}
              onChange={(e) => onChange(updateItem(draft, 'toolDoubles', i, { response: e.target.value }))} />
          </div>
        ))}
        {editable && (
          <Button size="small" icon={<PlusOutlined />} style={{ alignSelf: 'flex-start' }}
            onClick={() => onChange(addItem(draft, 'toolDoubles', { tool: '', outcome: 'Succeeds', response: '' }))}>
            Add a tool answer
          </Button>
        )}
      </div>
    </section>
  );
}

// ── Controls ────────────────────────────────────────────────────────────────

/** The server's word on one control; null while the version does not compile (nothing was replayed). */
export function ControlVerdictLine({ verdict, compiled }: { verdict?: ControlVerdict; compiled: boolean }) {
  if (!compiled) return <StatusPill tone="slate">Not replayed: fix the problems first</StatusPill>;
  if (!verdict) return <StatusPill tone="slate">Not replayed yet</StatusPill>;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
      <StatusPill tone={verdict.behaves ? 'green' : 'amber'}>{verdict.behaves ? 'Behaves' : 'Does not behave'}</StatusPill>
      <Text style={{ fontSize: 12.5 }}>{verdict.observed}</Text>
    </div>
  );
}

export function ControlsSection({ draft, onChange, editable, isDark, check }: SectionProps) {
  const controls = draft.controls ?? [];
  const turns = turnOptions(draft);
  const compiled = !!check && (check.problems ?? []).length === 0 && check.controls != null;
  const verdictOf = new Map((check?.controls ?? []).map((v) => [v.controlId, v]));
  const misbehaving = (check?.controls ?? []).filter((v) => !v.behaves).length;
  const add = (kind: ControlKind) => onChange(addItem(draft, 'controls', { id: newControlId(draft), kind, turnIndex: turns[0]?.value ?? 0, reply: '' }));
  return (
    <section id="section-controls" aria-label="Controls">
      <Text style={sectionTitle}>Controls</Text>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Replies the agent did not give, replayed against this version’s checks: a safe reply they must pass, and an unsafe one
        they must fail. A control that does not behave shows a check too weak to trust. It is a warning: it never blocks a save
        or an approval.
      </Text>
      {misbehaving > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} title={`${misbehaving} of ${controls.length} control(s) do not behave`}
          description="The checks pass an unsafe reply or fail a safe one. Strengthen the check the control names, or the control itself if it is wrong." />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {controls.length === 0 && <Text type="secondary">No control: nothing shows yet that these checks tell a right reply from a wrong one.</Text>}
        {controls.map((c, i) => (
          <div key={c.id} id={`control-${c.id}`} style={{ ...cardStyle(isDark), padding: 10, borderLeft: `3px solid ${c.kind === 'UnsafeMutation' ? colors.red[500] : colors.green[600]}` }}
            aria-label={`Control ${c.id}`}>
            <div style={row}>
              <Select<ControlKind> size="small" disabled={!editable} value={c.kind} aria-label="Kind of control"
                onChange={(v) => onChange(updateItem(draft, 'controls', i, { kind: v }))}
                options={(Object.keys(CONTROL_WORDS) as ControlKind[]).map((k) => ({ value: k, label: CONTROL_WORDS[k].label }))} />
              <Select<number> size="small" disabled={!editable} value={c.turnIndex} aria-label="Replayed on turn"
                onChange={(v) => onChange(updateItem(draft, 'controls', i, { turnIndex: v }))} options={turns} />
              <RemoveButton label={`Remove control ${c.id}`} editable={editable} onClick={() => onChange(removeItem(draft, 'controls', i))} />
            </div>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, margin: '6px 0 4px' }}>{CONTROL_WORDS[c.kind]?.detail}</Text>
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} disabled={!editable} value={c.reply} aria-label="The reply replayed"
              placeholder={c.kind === 'UnsafeMutation' ? 'A wrong reply, e.g. Rest tonight and call us in the morning if it is still high.' : 'A right reply in other words, e.g. Please call us now; a fever this week cannot wait.'}
              onChange={(e) => onChange(updateItem(draft, 'controls', i, { reply: e.target.value }))} />
            {(c.toolCalls ?? []).length > 0 && (
              <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                Replayed with {c.toolCalls!.length} tool call(s): {c.toolCalls!.map((t) => t.tool).join(', ')}. They are kept as they are; this screen does not edit them.
              </Text>
            )}
            <Input size="small" disabled={!editable} placeholder="Why this reply is safe or unsafe (optional)" value={c.note ?? ''} aria-label="Control note"
              style={{ marginTop: 6 }} onChange={(e) => onChange(updateItem(draft, 'controls', i, { note: e.target.value || undefined }))} />
            <div style={{ marginTop: 8 }}><ControlVerdictLine verdict={verdictOf.get(c.id)} compiled={compiled} /></div>
          </div>
        ))}
        {editable && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button size="small" icon={<PlusOutlined />} onClick={() => add('SafeParaphrase')}>Add a safe reply</Button>
            <Button size="small" icon={<PlusOutlined />} onClick={() => add('UnsafeMutation')}>Add an unsafe reply</Button>
          </div>
        )}
      </div>
    </section>
  );
}

// ── Siblings ────────────────────────────────────────────────────────────────

export function SiblingsSection({ draft, onChange, editable, isDark }: SectionProps) {
  const relations = draft.relations ?? [];
  return (
    <section id="section-siblings" aria-label="Siblings">
      <Text style={sectionTitle}>Siblings</Text>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Other scenarios this one varies or is varied by. Links only: a run executes nothing here, so a version whose only change
        is its links stays eligible for a batch approval.
      </Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {relations.length === 0 && <Text type="secondary">No sibling is linked.</Text>}
        {relations.map((r, i) => (
          <div key={i} style={{ ...cardStyle(isDark), padding: 10 }} aria-label={`Sibling ${i + 1}`}>
            <div style={row}>
              <Select<RelationKind> size="small" disabled={!editable} value={r.kind} aria-label="How it relates"
                onChange={(v) => onChange(updateItem(draft, 'relations', i, { kind: v }))}
                options={(Object.keys(RELATION_WORDS) as RelationKind[]).map((k) => ({ value: k, label: RELATION_WORDS[k].label }))} />
              <Input size="small" disabled={!editable} placeholder="The sibling’s scenario id" value={r.scenarioId} aria-label="Sibling scenario"
                style={{ fontFamily: "'JetBrains Mono', monospace" }} onChange={(e) => onChange(updateItem(draft, 'relations', i, { scenarioId: e.target.value }))} />
              <RemoveButton label={`Remove sibling ${i + 1}`} editable={editable} onClick={() => onChange(removeItem(draft, 'relations', i))} />
            </div>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, margin: '6px 0 4px' }}>{RELATION_WORDS[r.kind]?.detail}</Text>
            <Input size="small" disabled={!editable} placeholder="What differs (optional)" value={r.note ?? ''} aria-label="Sibling note"
              onChange={(e) => onChange(updateItem(draft, 'relations', i, { note: e.target.value || undefined }))} />
          </div>
        ))}
        {editable && (
          <Button size="small" icon={<PlusOutlined />} style={{ alignSelf: 'flex-start' }}
            onClick={() => onChange(addItem(draft, 'relations', { kind: 'Paraphrase', scenarioId: '' }))}>
            Link a sibling
          </Button>
        )}
      </div>
    </section>
  );
}
